if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      /*
        Nested <details> inside the outer hamburger <details> breaks native toggles on mobile WebKit
        and some legacy Android. Toggle <details.open> in JS only.

        - pointerdown: primary path on modern iPhone / Android (stops duplicate ghost click).
        - click: keyboard / screen readers, and old browsers without PointerEvent (e.g. Galaxy S III era).
      */
      this.menu.querySelectorAll('details.link-container > summary.parent-link').forEach((summary) => {
        const details = summary.parentElement;
        if (!details || details.tagName !== 'DETAILS') return;

        const toggleSubmenuDetails = () => {
          if (details.hasAttribute('open')) {
            details.removeAttribute('open');
            details.classList.remove('submenu-open');
          } else {
            details.setAttribute('open', '');
            details.classList.add('submenu-open');
          }
        };

        let suppressFollowingClick = false;
        const capOpts = { capture: true };

        if (typeof window.PointerEvent !== 'undefined') {
          summary.addEventListener(
            'pointerdown',
            (e) => {
              if (e.pointerType === 'mouse' && e.button !== 0) return;
              e.preventDefault();
              e.stopPropagation();
              toggleSubmenuDetails();
              suppressFollowingClick = true;
              window.setTimeout(() => {
                suppressFollowingClick = false;
              }, 500);
            },
            capOpts
          );
        } else {
          /* Very old stock Android browser: no PointerEvent; touchend before synthetic click */
          summary.addEventListener(
            'touchend',
            (e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSubmenuDetails();
              suppressFollowingClick = true;
              window.setTimeout(() => {
                suppressFollowingClick = false;
              }, 500);
            },
            { capture: true, passive: false }
          );
        }

        summary.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (suppressFollowingClick) return;
            toggleSubmenuDetails();
          },
          capOpts
        );
      });

      // Sticky Header Class
      window.addEventListener('scroll', this.setStickyClass.bind(this), {
        passive: true
      });

      // Mobile Menu offset
      window.addEventListener('scroll', this.setHeaderOffset.bind(this), {
        passive: true
      });
      window.addEventListener('scroll', this.setHeaderHeight.bind(this), {
        passive: true
      });
      window.addEventListener('resize', this.setHeaderHeight.bind(this));

      window.dispatchEvent(new Event('scroll'));

      if (document.querySelector('.announcement-bar-section')) {
        this.setAnnouncementHeight();
        window.addEventListener('scroll', this.setAnnouncementHeight.bind(this), {
          passive: true
        });
        window.addEventListener('resize', this.setAnnouncementHeight.bind(this));
        window.dispatchEvent(new Event('resize'));
      }

    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        const scrollThreshold = 50;
        const hasScrolled = window.scrollY > scrollThreshold;
        if (hasScrolled) {
          this.classList.add('is-sticky');
        } else {
          this.classList.remove('is-sticky');
        }
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      const h = a_bar ? a_bar.clientHeight : 0;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      const rect = this.getBoundingClientRect();
      document.documentElement.style.setProperty('--header-height', rect.height + 'px');
      /* Viewport Y of header bottom edge — matches sticky/in-flow header; avoids gap vs sum(announcement + height) */
      document.documentElement.style.setProperty('--header-bottom', rect.bottom + 'px');
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('submenu-open');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll(
        '.thb-full-menu > li.menu-item-has-children:not(.menu-item-has-megamenu) > details > .sub-menu'
      );
    }
    connectedCallback() {
      const _this = this;
      this.initDesktopDetailsNav();
      this.initNestedFlyoutDetails();

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      document.fonts.ready.then(function () {
        window.addEventListener('resize', debounce(function () {
          _this.resizeSubMenus();
        }, 100));
        _this.resizeSubMenus();
      });

    }
    /**
     * Desktop: top-level nav uses <details>/<summary> (see header-full-menu.liquid).
     * Click-activated accordion; outside click and Escape close.
     */
    initDesktopDetailsNav() {
      if (this._desktopDetailsNavBound) {
        return;
      }
      const topDetailsEls = () =>
        this.querySelectorAll('.thb-full-menu > li.menu-item-has-children > details.thb-full-menu__details');

      if (!topDetailsEls().length) {
        return;
      }
      this._desktopDetailsNavBound = true;

      const mq = window.matchMedia('(min-width: 1068px)');
      const closeAllDetails = () => {
        topDetailsEls().forEach((d) => d.removeAttribute('open'));
      };

      const escapeKeyDismiss = (e) => {
        return (
          e.key === 'Escape' ||
          e.key === 'Esc' ||
          e.code === 'Escape' ||
          e.keyCode === 27
        );
      };

      topDetailsEls().forEach((details) => {
        const summary = details.querySelector('summary.thb-full-menu__summary');
        if (!summary || !this.contains(summary)) {
          return;
        }

        const toggleTopDetails = () => {
          if (details.hasAttribute('open')) {
            details.removeAttribute('open');
            return;
          }
          topDetailsEls().forEach((other) => {
            if (other !== details) {
              other.removeAttribute('open');
            }
          });
          details.setAttribute('open', '');
        };

        let userActivated = false;
        const activateTopDetails = () => {
          userActivated = true;
          toggleTopDetails();
        };

        summary.addEventListener(
          'click',
          (e) => {
            if (!mq.matches) {
              return;
            }
            e.preventDefault();
            activateTopDetails();
          },
          true
        );

        summary.addEventListener(
          'keydown',
          (e) => {
            if (!mq.matches) {
              return;
            }
            if (e.key !== 'Enter' && e.key !== ' ') {
              return;
            }
            e.preventDefault();
            activateTopDetails();
          },
          true
        );

        details.addEventListener('toggle', () => {
          if (!mq.matches) {
            return;
          }
          if (details.open && !userActivated) {
            details.removeAttribute('open');
            return;
          }
          userActivated = false;
          if (details.open) {
            topDetailsEls().forEach((other) => {
              if (other !== details) {
                other.removeAttribute('open');
              }
            });
          } else {
            details
              .querySelectorAll('.thb-full-menu__nested-details[open]')
              .forEach((nested) => nested.removeAttribute('open'));
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (!mq.matches) {
          return;
        }
        if (this.contains(e.target)) {
          return;
        }
        closeAllDetails();
      });

      document.addEventListener(
        'keydown',
        (e) => {
          if (!escapeKeyDismiss(e) || !mq.matches) {
            return;
          }
          const openNested = this.querySelector(
            '.thb-full-menu .sub-menu > li.menu-item-has-children > details.thb-full-menu__nested-details[open]'
          );
          if (openNested && openNested.contains(document.activeElement)) {
            const nestedSummary = openNested.querySelector('summary.thb-full-menu__nested-summary');
            e.preventDefault();
            openNested.removeAttribute('open');
            if (nestedSummary && this.contains(nestedSummary)) {
              window.requestAnimationFrame(() => {
                nestedSummary.focus({ preventScroll: true });
              });
            }
            return;
          }
          const openDetails = this.querySelector(
            '.thb-full-menu > li.menu-item-has-children > details[open].thb-full-menu__details'
          );
          if (!openDetails) {
            return;
          }
          const summary = openDetails.querySelector('summary.thb-full-menu__summary');
          const focusWasInside = openDetails.contains(document.activeElement);
          e.preventDefault();
          closeAllDetails();
          if (focusWasInside && summary && this.contains(summary)) {
            window.requestAnimationFrame(() => {
              summary.focus({ preventScroll: true });
            });
          }
        },
        true
      );

      mq.addEventListener('change', () => {
        if (!mq.matches) {
          closeAllDetails();
        }
      });
    }
    /**
     * Nested flyouts (e.g. Other Groups under Get Involved): click-activated <details>.
     */
    initNestedFlyoutDetails() {
      if (this._nestedFlyoutDetailsBound) {
        return;
      }
      const nestedDetailsEls = () =>
        this.querySelectorAll(
          '.thb-full-menu .sub-menu > li.menu-item-has-children > details.thb-full-menu__nested-details'
        );

      if (!nestedDetailsEls().length) {
        return;
      }
      this._nestedFlyoutDetailsBound = true;

      const mq = window.matchMedia('(min-width: 1068px)');

      const closeSiblingNestedDetails = (details) => {
        const parentSubmenu = details.closest('.sub-menu');
        if (!parentSubmenu) {
          return;
        }
        parentSubmenu
          .querySelectorAll(
            ':scope > li.menu-item-has-children > details.thb-full-menu__nested-details[open]'
          )
          .forEach((other) => {
            if (other !== details) {
              other.removeAttribute('open');
            }
          });
      };

      nestedDetailsEls().forEach((details) => {
        const summary = details.querySelector('summary.thb-full-menu__nested-summary');

        if (summary) {
          const toggleNestedDetails = () => {
            if (details.hasAttribute('open')) {
              details.removeAttribute('open');
              return;
            }
            closeSiblingNestedDetails(details);
            details.setAttribute('open', '');
          };

          let userActivated = false;
          const activateNestedDetails = () => {
            userActivated = true;
            toggleNestedDetails();
          };

          summary.addEventListener(
            'click',
            (e) => {
              if (!mq.matches) {
                return;
              }
              e.preventDefault();
              activateNestedDetails();
            },
            true
          );

          summary.addEventListener(
            'keydown',
            (e) => {
              if (!mq.matches) {
                return;
              }
              if (e.key !== 'Enter' && e.key !== ' ') {
                return;
              }
              e.preventDefault();
              activateNestedDetails();
            },
            true
          );

          details.addEventListener('toggle', () => {
            if (!mq.matches) {
              return;
            }
            if (details.open && !userActivated) {
              details.removeAttribute('open');
              return;
            }
            userActivated = false;
            if (!details.open) {
              return;
            }
            closeSiblingNestedDetails(details);
          });
        }
      });

      mq.addEventListener('change', () => {
        if (!mq.matches) {
          nestedDetailsEls().forEach((d) => d.removeAttribute('open'));
        }
      });
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(
          ':scope > .menu-item-has-children > details.thb-full-menu__nested-details > .sub-menu'
        );

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}